<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance, defineAsyncComponent, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import _ from "lodash"
import moment from "moment"
import Spinner from "@/components/ui/Spinner.vue"
import PaywallService from "@/services/PaywallService";
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import AlertModal from '@/components/modals/AlertModal.vue'
import IconBase from '@/components/icon/IconBase.vue'
import AddPaywallModal from "@/components/modals/AddPaywallModal.vue"
import PaywallTransactions from '@/components/Paywall/TransactionsTab.vue'
import PaywallCustomers from '@/components/Paywall/CustomersTab.vue'
import PaywallEmailAccess from '@/components/Paywall/PaywallEmailAccess.vue'
import StreamSourceDropdown from "@/components/ui/StreamSourceDropdown.vue"
import PaywallPlaceholder from "../../components/Paywall/PaywallPlaceholder.vue"
import IconLinkExternal from "../../components/icon/IconLinkExternal.vue"
import IconBin from "../../components/icon/IconBin.vue"
import PaywallNav from "../../components/Paywall/PaywallNav.vue"
import PaywallDashboardGrid from "../../components/Paywall/PaywallDashboardGrid.vue"
import PaywallHeader from "../../components/Paywall/PaywallHeader.vue"
import BackButton from '@/components/Atoms/BackButton.vue'
import PaywallInterface from '../../components/Paywall/PaywallInterface.vue'
import PaywallSettings from '../../components/Paywall/PaywallSettings.vue'
import PaywallCallToAction from '../../components/Paywall/PaywallCallToAction.vue'
import PaywallSessionLimit from '../../components/Paywall/PaywallSessionLimit.vue'
import PaywallSharedAccess from '../../components/Paywall/PaywallSharedAccess.vue'
import PaywallPromo from '../../components/Paywall/PaywallPromo.vue'
import PaywallLivePreview from '../../components/Paywall/PaywallLivePreview.vue'
import FormButton from "../../components/Atoms/FormButton.vue"
import FormInput from "../../components/Atoms/FormInput.vue"
import FormSelect from "../../components/Atoms/FormSelect.vue"
import TabGroup from "../../components/Atoms/TabGroup.vue"
import TabItem from "../../components/Atoms/TabItem.vue"
import IconEdit from "../../components/icon/IconEdit.vue"
import IconCalendar from "../../components/icon/IconCalendar.vue"
import IconClock from "../../components/icon/IconClock.vue"
import VueDatePicker from '@vuepic/vue-datepicker';
import IconInfoSmall from "../../components/icon/IconInfoSmall.vue"
import IconCheckmark from "../../components/icon/IconCheckmark.vue"
import Tooltip from '../../components/Atoms/Tooltip.vue'
import { useNotification } from "@kyvg/vue3-notification"
import Badge from '../../components/Badge.vue'
import ButtonCopyToClipboard from '../../components/Moleculas/ButtonCopyToClipboard.vue'
import { formatMoney } from '@/Utils/money'
import { track, EVENTS } from '@/lib/tracking'
import { hasProductAccess } from '@/Utils/teamPermissionRows'
import PageHeader from '@/components/ui/PageHeader.vue'
import NoAccessState from '@/components/ui/NoAccessState.vue'
// Lazy so TipTap stays out of the main paywall bundle; rendered inline (same
// route) so switching Paywalls <-> Settings never remounts/reloads the dashboard.
const PaywallGlobalSettings = defineAsyncComponent(() =>
  import(/* webpackChunkName: "paywall-settings", webpackPrefetch: true */ './PaywallGlobalSettings.vue'))
// Same chunk: also uses TipTap (the "About this event" editor).
const PaywallEventPage = defineAsyncComponent(() =>
  import(/* webpackChunkName: "paywall-settings", webpackPrefetch: true */ '../../components/Paywall/PaywallEventPage.vue'))

const instance = getCurrentInstance();
const root = instance.proxy.$root;

const store = useStore()
const router = useRouter()
const route = useRoute()

const { notify } = useNotification()

const resStep = ref(1)
const dividerPosition = ref(25)
// Set before the first paint so a detail URL does not flash the grid while
// onMounted waits on the list. The grid is the view only when the route has no id.
const currentPaywallIndex = ref(route.params.paywallId || null)
const tempLinks = ref([])
const tempLinkDateTime = ref(null)
const tempLinkMaxDateTime = ref(null)
const tempLinkMinDateTime = ref(null)
const showTempLinkDatePicker = ref(false)
const tempLinkProcessing = ref(false)
const iframe = ref('responsive')
const logoSrc = ref(null)
const companyName = ref('')
const companyUrl = ref('')
const companyEmail = ref('')
const showEditPriceInput = ref(false)
const showEditNameInput = ref(false)
const savingName = ref(false)
const paywallNameInput = ref(null)
const paywallName = ref('')
const paywallPrice = ref(null)
// Backend price is Decimal(8,2); values above this overflow / are rejected by Stripe.
const MAX_PRICE = 999999.99
// "Free" is just a $0 paywall; the selector mirrors the create modal so existing
// paywalls can be switched between paid and free.
const editPaymentType = ref('payperview')
const paymentTypeOptions = [
  { text: 'Pay per view', value: 'payperview' },
  { text: 'Free', value: 'free' },
]
const syncEditPaymentType = () => {
  editPaymentType.value = Number(paywallPrice.value) > 0 ? 'payperview' : 'free'
}

// Access length (rental window). access_seconds null/0 = lifetime.
const ACCESS_UNIT_SECONDS = { hour: 3600, day: 86400, week: 604800, month: 2592000 }
const editAccessLength = ref('lifetime') // 'lifetime' | 'limited'
const editAccessValue = ref(48)
const editAccessUnit = ref('hour')
// Optional event-anchored access start (UTC ISO string, null = starts at purchase).
const editAccessStartAt = ref(null)
const savingAccess = ref(false)
const accessLengthOptions = [
  { text: 'Lifetime', value: 'lifetime' },
  { text: 'Limited time', value: 'limited' },
]
const accessUnitOptions = [
  { text: 'Hours', value: 'hour' },
  { text: 'Days', value: 'day' },
  { text: 'Weeks', value: 'week' },
  { text: 'Months', value: 'month' },
]
const syncEditAccessLength = () => {
  const secs = Number(currentPaywall.value?.access_seconds) || 0
  if (secs > 0) {
    editAccessLength.value = 'limited'
    const unit = [['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600]]
      .find(([, size]) => secs % size === 0) || ['hour', 3600]
    editAccessUnit.value = unit[0]
    editAccessValue.value = secs / unit[1]
  } else {
    editAccessLength.value = 'lifetime'
    editAccessValue.value = 48
    editAccessUnit.value = 'hour'
  }
  // Event-anchored start is only meaningful for a limited window; null = starts at purchase.
  editAccessStartAt.value = secs > 0 ? (currentPaywall.value?.access_start_at || null) : null
}
// The picked date/time is entered in the creator's local timezone; make that
// explicit next to the field (the stored value is converted to UTC).
const localTimezoneLabel = computed(() => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const off = -new Date().getTimezoneOffset()
    const sign = off >= 0 ? '+' : '-'
    const hh = String(Math.floor(Math.abs(off) / 60)).padStart(2, '0')
    const mm = String(Math.abs(off) % 60).padStart(2, '0')
    return `${tz} (GMT${sign}${hh}:${mm})`
  } catch { return 'your local timezone' }
})
const editAccessSeconds = computed(() => {
  if (editPaymentType.value === 'free' || editAccessLength.value !== 'limited') return null
  const v = Math.trunc(Number(editAccessValue.value))
  if (!Number.isFinite(v) || v <= 0) return null
  return v * (ACCESS_UNIT_SECONDS[editAccessUnit.value] || ACCESS_UNIT_SECONDS.hour)
})
const editAccessSummary = computed(() => {
  const v = Math.trunc(Number(editAccessValue.value))
  const n = Number.isFinite(v) && v > 0 ? v : 0
  return `${n} ${editAccessUnit.value}${n === 1 ? '' : 's'}`
})
const paywallReports = ref(null)
const sourceConflictCheck = ref(false)
const stripeConnectStatusChecker = ref(null)
const stripeConnectProcessing = ref(false)
const stripeConnectLinkError = ref(null)
const stripeOauthLink = ref(null)
const popupAuth = ref(false)
const paywallActive = ref(false)
const streamSourceDropdownVisible = ref(false)
const sourceSaving = ref(false)
const sourceJustSaved = ref(false)
let sourceSavedTimeout = null
// Bumped to force the live-preview iframe to remount and re-fetch after edits (price, etc.)
const previewKey = ref(0)

// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const userloaded = computed(() => store.getters['User/userLoaded'])
const activeTeam = computed(() => store.state.User.activatedTeam)
const userPermissions = computed(() => store.getters['User/userPermissions'])
const canAccessPaywall = computed(() => !activeTeam.value || hasProductAccess(userPermissions.value, 'paywall'))
const ownerEmail = computed(() => store.getters['User/teamOwner']?.email || '')
const paywallStripeCommission = computed(() => store.getters['User/paywallStripeCommission'])
const maxPaywalls = computed(() => store.getters['User/maxPaywalls'])
const paywalls = computed(() => store.getters['Paywalls/paywalls'])
const currentPaywall = computed(() => store.getters['Paywalls/currentPaywall'])

// Browser tab title: "<Paywall name> - Paywall - Castr" (App.vue appends " - Castr").
const emit = defineEmits(['updateTitle'])
watch(currentPaywall, (p) => {
  const name = p?.internalName || p?.title
  emit('updateTitle', name ? `${name} - Paywall` : 'Paywall')
}, { immediate: true })

// Signature of every field that changes what viewers see in the live preview.
// When any of these change (via store updates after a save), we remount the
// preview iframe so it re-fetches the latest paywall config.
const previewSignature = computed(() => {
  const p = currentPaywall.value
  if (!p) return ''
  return JSON.stringify({
    title: p.title,
    cta_text: p.cta_text,
    cta_free_text: p.cta_free_text,
    login_text: p.login_text,
    wallpaper_url: p.wallpaper_url,
    price: p.price,
    currency: p.currency,
    access_seconds: p.access_seconds,
    company_name: p.company_name,
    company_url: p.company_url,
    company_email: p.company_email,
    company_logo: p.company_logo,
    // PaywallSettings updates the store optimistically with camelCase keys
    companyName: p.companyName,
    companyUrl: p.companyUrl,
    companyEmail: p.companyEmail,
    max_sessions: p.max_sessions,
    source: p.source?._id || p.source?.key || null,
    enabled: p.enabled,
  })
})

let previewRefreshTimer = null
watch(previewSignature, () => {
  clearTimeout(previewRefreshTimer)
  // small debounce so back-to-back store writes collapse into one reload
  previewRefreshTimer = setTimeout(() => { previewKey.value++ }, 300)
})

const userPaywallActived = computed(() => store.getters['User/userPaywallActived'])
const userPaywall = computed(() => store.getters['User/userPaywall'])

// Grid (list) vs detail: no :paywallId in the route → show the card-grid dashboard
const showGrid = computed(() => !currentPaywallIndex.value)
// Global settings are shown inline on the same route via ?view=settings, so the
// tab switch is instant and the dashboard data isn't reloaded.
const showSettings = computed(() => showGrid.value && route.query.view === 'settings')
// List chrome (title) paints before paywall/Stripe data. Skip on detail and
// settings — those views own their own headings. Skip when the grid is showing
// because PaywallDashboardGrid already renders PageHeader with Stripe/New.
const showPaywallChromeHeader = computed(() => {
	if (!showGrid.value || showSettings.value) return false
	if (loadingStatus.value || !userloaded.value) return true
	if (!canAccessPaywall.value) return true
	const showingGrid = (paywalls.value?.length > 0) && !isStripePending.value
	return !showingGrid
})
const openSettings = () => {
  router.replace({ query: { ...route.query, view: 'settings' } }).catch(() => { })
}
const closeSettings = () => {
  const q = { ...route.query }
  delete q.view
  router.replace({ query: q }).catch(() => { })
}
const deleteTargetId = ref(null)
// Keep the full paywall object for the delete flow so we can offer to disable
// it when deletion is blocked by existing payments.
const deleteTargetPaywall = ref(null)
const paywallUsers = ref([])
const paywallTransactionsAll = ref([])

// A single VOD file source is returned with type 'vod' but flagged via
// `sourceType: 'vod-file'` (with the file details under `vodFile`), whereas a
// whole VOD folder has no `vodFile`. Its `name` is the parent folder, so use
// the file name for display.
// Two shapes: API returns { type:'vod', sourceType:'vod-file', vodFile:{...} };
// a freshly-selected file from the widget is a raw VOD file with `fileName`
// (and no `streams`, which would indicate a playlist).
const isSingleVodFileSource = (s) =>
  !!s && (s.sourceType === 'vod-file' || !!s.vodFile || (!!s.fileName && !s.streams))

const sourceDisplayName = (s) => {
  if (!s) return ''
  // DVR / Live-to-VOD clips carry their name in `vodFile.name` (not `fileName`);
  // fall back to that before the parent stream's `name`.
  if (isSingleVodFileSource(s)) return s.vodFile?.fileName || s.vodFile?.name || s.fileName || s.name || ''
  return s.name || ''
}

const sourceTypeLabel = (s) => {
  if (!s) return ''
  if (s.streams) return 'Playlist'
  if (isSingleVodFileSource(s)) return 'VOD file'
  switch (s.type) {
    case 'live': return 'Livestream'
    case 'restream': return 'Multistream'
    case 'vod': return 'VOD'
    case 'ipcam': return 'IP Camera'
    case 'subsecond': return 'Sub Second'
    default: return s.type || 'Source'
  }
}

const paywallSubtitle = computed(() => {
  const p = currentPaywall.value
  if (!p) return ''
  const parts = []
  if (p.source) {
    parts.push(sourceTypeLabel(p.source))
    const name = sourceDisplayName(p.source)
    if (name) parts.push(name)
  } else {
    parts.push('No source')
  }
  parts.push(formatMoney(p.price, p.currency))
  return parts.join(' · ')
})

// A user belongs to the master and carries an invoices[] array (each invoice
// has the stream_id) — there is no top-level u.stream_id. Count distinct users
// who have at least one (non-pending) invoice for this paywall's stream.
const customersCount = computed(() => {
  const sid = String(currentPaywall.value?.id ?? '')
  if (!sid) return 0
  return _.filter(paywallUsers.value || [], u =>
    _.some(u.invoices || [], inv => String(inv.stream_id) === sid)
  ).length
})

const currencyUpper = computed(() => (currentPaywall.value?.currency || '').toUpperCase())

const protectedUrl = computed(() => (currentPaywall.value?._streamUrl || '').replace('_disabled', ''))

const PAYWALL_EMBED_SCRIPT = 'https://watch.castr.io/embed.js'

// Embed master/stream IDs are encoded in the protected URL: /<master>/stream/<stream>
const embedIds = computed(() => {
  const m = protectedUrl.value.match(/\/([^/]+)\/stream\/([^/?#]+)/i)
  return { master: m?.[1] || '', stream: m?.[2] || '' }
})

const paywallEmbedCode = computed(() => {
  const { master, stream } = embedIds.value
  if (!master || !stream) return ''
  return `<div class="castr-paywall" data-master="${master}" data-stream="${stream}"></div>\n<script src="${PAYWALL_EMBED_SCRIPT}" async><\/script>`
})

const embedPreviewSrcdoc = computed(() => {
  const { master, stream } = embedIds.value
  if (!master || !stream) return ''
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>html,body{margin:0;padding:0;background:transparent;}</style></head><body><div class="castr-paywall" data-master="${master}" data-stream="${stream}"></div><script src="${PAYWALL_EMBED_SCRIPT}" async><\/script></body></html>`
})

const paymentsCount = computed(() =>
  _.filter(paywallTransactionsAll.value || [], t =>
    String(t.stream_id) === String(currentPaywall.value?.id) && (t.status === 'active' || t.status === 'refund')
  ).length
)

const refundsCount = computed(() =>
  _.filter(paywallTransactionsAll.value || [], t =>
    String(t.stream_id) === String(currentPaywall.value?.id) && t.status === 'refund'
  ).length
)

const paywallInfo = ref(null)
const isStripePending = computed(
  () => paywallInfo.value?.stripe_account_status === 'pending',
)

/** Revenue stats 404 until Stripe is connected; skip them on the onboarding screen. */
const canFetchFinancialReport = computed(() => {
  if (isStripePending.value) return false
  if (paywallInfo.value?.stripe_account_status === 'active') return true
  if (userPaywallActived.value) return true
  // Single-owner sessions may have no paywall object on the user record;
  // existing streams are the same signal the dashboard already uses.
  return (paywalls.value?.length || 0) > 0
})

const totalPayments = ref(null)
const totalRefunds = ref(null)
const totalEarnings = ref(null)

const loadPaywallInfo = async () => {
  // Load whenever a paywall mapping exists (not only when locally "linked"), so
  // we can reconcile the linked flag with the account's real Stripe status.
  if (!userPaywall.value) {
    paywallInfo.value = null
    return
  }
  try {
    paywallInfo.value = await PaywallService.getPaywall()
    // Keep the client's linked flag in sync with the real account status.
    store.dispatch('User/setPaywallLinked', paywallInfo.value?.stripe_account_status === 'active')
  } catch (err) {
    console.error(err)
    paywallInfo.value = null
  }
}

// Earnings, customers, and transactions fill the cards after the list is visible.
const loadPaywallStats = async () => {
  if (!canFetchFinancialReport.value) {
    paywallReports.value = []
    return
  }
  const [reports, users, transactions] = await Promise.all([
    PaywallService.getPaywallFinancialReport().catch(() => []),
    PaywallService.getUsers().catch(() => []),
    PaywallService.getTransactions().catch(() => []),
  ])
  paywallReports.value = reports || []
  paywallUsers.value = users || []
  paywallTransactionsAll.value = transactions || []
  displayPaywallReport()
}

const paywallIframeCode = computed(() => {
  if (!currentPaywall.value) return ''

  const url = 'https://player.castr.com/' + (currentPaywall.value.source.streams ? 'playlist/' + currentPaywall.value.source._id : currentPaywall.value.source.key)
  if (iframe.value === 'responsive') {
    return `<div style="position: relative; padding-bottom: 56.25%; height: 0;"><iframe src="${url}" frameborder="0" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`
  }
  return `<iframe width="640" height="360" src="${url}" frameborder="0" allowfullscreen></iframe>`
})

onMounted(async () => {
  if (route.query.test === 'test') {
    console.log('Query param test=test exists');
  }
  const hadList = store.getters['Paywalls/isLoaded']
  // The spinner covers only the first load of the list. A return visit paints
  // the stored paywalls and refreshes them without hiding the page.
  if (!hadList) {
    store.dispatch('Ui/setLoadingStatus', true)
  } else {
    store.dispatch('Ui/setLoadingStatus', false)
  }
  if (!store.getters['User/userLoaded']) {
    await store.dispatch('User/loadUser')
  }
  if (!canAccessPaywall.value) {
    await store.dispatch('User/getinfoOwner')
    store.dispatch('Ui/setLoadingStatus', false)
    return
  }
  // Source picker data. Both return immediately once loaded, and neither
  // decides what the list or the detail shell shows.
  store.dispatch('Streams/getStreams').catch(() => {})
  store.dispatch('Playlist/getPlaylist').catch(() => {})

  const paywallReady = !!currentPaywall.value
  if (paywallReady) selectPaywall(currentPaywallIndex.value)

  try {
    if (hadList) {
      store.dispatch('Paywalls/getPaywalls', { sort: 'desc', forceRefresh: true }).then(() => {
        if (!paywallReady && currentPaywallIndex.value) selectPaywall(currentPaywallIndex.value)
      }).catch((err) => {
        console.error(err)
      })
      loadPaywallInfo().then(() => loadPaywallStats())
    } else {
      await Promise.all([
        store.dispatch('Paywalls/getPaywalls', 'desc'),
        loadPaywallInfo(),
      ])
    }
  } catch (err) {
    console.error(err)
  } finally {
    store.dispatch('Ui/setLoadingStatus', false)
  }

  if (!paywallReady && currentPaywallIndex.value) selectPaywall(currentPaywallIndex.value)
  paywallActive.value = userPaywallActived.value
  if (!hadList) loadPaywallStats()
  document.addEventListener('click', componentClickHandler())

  setTimeout(() => {
    store.dispatch('Ui/setLoadingStatus', false)
  }, 2000);
})

onBeforeUnmount(() => {
  clearInterval(stripeConnectStatusChecker.value)
  clearTimeout(sourceSavedTimeout)
  clearTimeout(previewRefreshTimer)
  // document.removeEventListener('mousemove', onDrag)
})

// Watchers
watch(() => route.params.paywallId, (newId) => {
  selectPaywall(newId)
})

const togglePopupAuthAlert = () => {
  sourceConflictCheck.value = false
  if (!currentPaywall.value.popupAuth) {
    sourceConflictCheck.value = true
  }

  togglePopupAuth()
}

const togglePopupAuthCancel = () => {
  popupAuth.value = false
}
const onPaywallTogge = async () => {
  if (popupAuth.value) {
    await togglePopupAuth(false)
  }
}
const togglePopupAuth = async (showNotify = true) => {
  try {
    const resp = await PaywallService.toggleStreamPaywallPopupAuth(currentPaywall.value.id, sourceConflictCheck.value);
    popupAuth.value = resp.popupAuth

    store.dispatch('Paywalls/setPaywallProp', { id: currentPaywall.value.id, prop: 'popupAuth', value: popupAuth.value })

    if (showNotify) {
      notify({
        group: "success",
        text: `Paywall protected player iframe updated`,
      });
    }
  } catch (err) {
    if (err.message === 'source conflict') {
      sourceConflictCheck.value = false
      return showModal('toggle-popupauth')
    }
    notify({
      group: "error",
      text: err.message || "could not update paywall protected player iframe",
    });
  }
}

const displayPaywallReport = () => {
  if (currentPaywall.value) {
    const report = _.find(paywallReports.value, { stream_id: currentPaywall.value.id })

    totalPayments.value = Number(_.get(report, 'payments_total', 0.00)).toFixed(2)
    totalRefunds.value = Number(_.get(report, 'refunds_total', 0.00)).toFixed(2)
    totalEarnings.value = (totalPayments.value - totalRefunds.value).toFixed(2)
  }
}

const onStripeConnectClick = async () => {
  stripeConnectLinkError.value = false;
  try {
    const pwMaster = await PaywallService.initPaywall()
    if (pwMaster.oauthLink) {
      stripeOauthLink.value = pwMaster.oauthLink
      stripeConnectProcessing.value = true

      monitorStripeLinkingStatus()
      window.open(stripeOauthLink.value)
      return
    }

    // No onboarding link means the account is already active/linked (e.g. a
    // reused account on relink, or onboarding finished in another tab) — refresh
    // so the dashboard reflects the linked state.
    notify({ group: "success", text: "Stripe account linked" })
    setTimeout(() => window.location.reload(), 300)
  } catch (err) {
    notify({
      group: "error",
      text: err.message || "Couldn't connect Stripe",
    });
  }
}

const monitorStripeLinkingStatus = async () => {
  stripeConnectStatusChecker.value = setInterval(async () => {
    try {
      const resp = await PaywallService.stripeLinkingStatus();

      if (resp.status === "active") {
        // init stream
        stripeOauthLink.value = null;
        stripeConnectLinkError.value = false;
        clearInterval(stripeConnectStatusChecker.value);
        window.location.reload();
      }

      if (resp.status === "failed") {
        stripeConnectProcessing.value = false;
        stripeConnectLinkError.value = true;
        clearInterval(stripeConnectStatusChecker.value);
      }
    } catch (e) {
      notify({
        group: "error",
        text: e.message || "Couldn't link your Stripe account",
      });
      clearInterval(stripeConnectStatusChecker.value);
    }
  }, 3000);
}

const onStripeConnectCancelClick = () => {
  clearInterval(stripeConnectStatusChecker.value)
  stripeOauthLink.value = null;
  stripeConnectProcessing.value = false
}

const onSourceDropdownHide = () => {
  streamSourceDropdownVisible.value = false
}

const onSourceDropdownShow = () => {
  streamSourceDropdownVisible.value = true
}

const onUnlinkStripe = async () => {
  try {
    await PaywallService.unlinkPaywall()

    notify({
      group: "success",
      text: "Stripe unlinked",
    });

    await store.dispatch('User/unloadPaywall')
    await store.dispatch('Paywalls/unloadPaywall')

    paywallActive.value = false

    setTimeout(() => {
      router.push({ path: '/paywall/v2/' }).catch(() => { });
    }, 300)

  } catch (err) {
    notify({
      group: "error",
      text: err.message || "Couldn't unlink Stripe",
    });
  }
}

const onSwitchStripeAccount = async () => {
  try {
    await PaywallService.unlinkPaywall(true)

    await store.dispatch('User/unloadPaywall')
    await store.dispatch('Paywalls/unloadPaywall')

    paywallActive.value = false

    notify({
      group: "success",
      text: "Disconnected. Connect a different Stripe account to continue.",
    });

    setTimeout(() => onStripeConnectClick(), 300)
  } catch (err) {
    notify({
      group: "error",
      text: err.message || "Couldn't switch Stripe account",
    });
  }
}

// Unlinking or switching the Stripe account tears down the paywall setup, so
// require the user to delete their paywalls first instead of doing it for them.
// These gate the confirm modals: with paywalls present we surface a blocking
// warning modal and never open the confirm modal (nor reach its cascade delete).
// The lead sentence differs per action; the instruction line is shared.
const stripeWarningMessage = ref('')
const stripeWarningBody = ref('Delete all existing paywalls before continuing')

// Sites bill through the same paywall account: unlinking pauses their paid
// plans (castr does this server-side), switching accounts is refused by castr
// (409 sites_bound_to_master) because the sites cannot follow a new master.
const sitesOnAccount = ref(0)
const loadSitesOnAccount = async () => {
  if (!store.getters['User/hasSitesFeature']) { sitesOnAccount.value = 0; return }
  try {
    const res = await store.dispatch('Sites/getSites')
    const sites = Array.isArray(res) ? res : res?.sites
    sitesOnAccount.value = (sites || []).filter((s) => s.billing?.paywallSiteId).length
  } catch {
    sitesOnAccount.value = 0
  }
}
const unlinkStripeBody = computed(() => {
  const base = 'This permanently removes all paywalls and their associated data. Your transaction history remains available in your Stripe dashboard.'
  if (!sitesOnAccount.value) return base
  const n = sitesOnAccount.value
  return `${base} Paid plans on your ${n} ${n === 1 ? 'site are' : 'sites are'} paused until you reconnect (viewers keep what they bought; paid content stays locked).`
})

const requestUnlinkStripe = async () => {
  if (paywalls.value?.length > 0) {
    stripeWarningMessage.value =
      'You cannot unlink your Stripe account while you have existing paywalls.'
    stripeWarningBody.value = 'Delete all existing paywalls before continuing'
    showModal('stripe-paywall-warning')
    return
  }
  await loadSitesOnAccount()
  showModal('unlink-stripe')
}

const requestSwitchStripe = async () => {
  if (paywalls.value?.length > 0) {
    stripeWarningMessage.value =
      'You cannot change your Stripe account while you have existing paywalls.'
    stripeWarningBody.value = 'Delete all existing paywalls before continuing'
    showModal('stripe-paywall-warning')
    return
  }
  await loadSitesOnAccount()
  if (sitesOnAccount.value > 0) {
    stripeWarningMessage.value =
      `You cannot switch Stripe accounts while ${sitesOnAccount.value} ${sitesOnAccount.value === 1 ? 'site is' : 'sites are'} billed through this account.`
    stripeWarningBody.value = 'Sites, their plans and viewers stay with the account they were created on. Unlink Stripe instead; paid plans pause until you reconnect.'
    showModal('stripe-paywall-warning')
    return
  }
  showModal('switch-stripe')
}

const componentClickHandler = (ev) => {
  if (streamSourceDropdownVisible.value ||
    !ev?.target?.parentElement?.className ||
    ev?.target?.parentElement.className.includes('toggle-popupauth') ||
    ev?.target?.parentElement.className.includes('custom-control')) {
    return
  }
  if (ev.target.className === 'custom-control-label') {
    showModal('unlink-stripe')
  }
}

const onPaywallRemove = async () => {
  const targetId = deleteTargetId.value || currentPaywall.value?.id
  if (!targetId) return
  try {
    await PaywallService.deleteStreamPaywall(targetId)

    notify({
      group: "success",
      text: "Paywall deleted",
    });

    store.dispatch('Paywalls/removePaywall', targetId)
    deleteTargetId.value = null

    // Return to the grid after deleting
    currentPaywallIndex.value = null
    router.push({ path: '/paywall/v2' }).catch(() => { });
  } catch (err) {
    // The backend blocks deletion when a paywall has payment history (records
    // are kept intact). Guide the user to disable it instead rather than
    // surfacing the raw, misleading "related payments are exists" text.
    const blockedByPayments = /payment/i.test(err?.message || '')
    if (blockedByPayments) {
      if (deleteTargetPaywall.value?.enabled) {
        showModal('paywall-has-payments')
      } else {
        notify({
          group: 'error',
          text: "This paywall can't be deleted because it has associated payments. Its transaction history is preserved.",
        })
      }
      return
    }
    notify({
      group: "error",
      text: err.message || "Couldn't delete paywall",
    });
  }
}

// Fallback when a paywall can't be deleted due to existing payments: disable it
// so it stops accepting new sign-ups while its records stay intact.
const disablePaywallAfterFailedDelete = async () => {
  const paywall = deleteTargetPaywall.value
  if (!paywall) return
  try {
    await PaywallService.toggleStreamPaywall(paywall.id)
    store.dispatch('Paywalls/setPaywallProp', { id: paywall.id, prop: 'enabled', value: false })
    notify({ group: 'success', text: 'Paywall disabled' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't disable paywall" })
  }
}

// Grid dashboard handlers
const openPaywall = (id) => {
  router.push({ path: '/paywall/v2/' + id }).catch(() => { })
}
const goToGrid = () => {
  currentPaywallIndex.value = null
  router.push({ path: '/paywall/v2' }).catch(() => { })
}
const togglePaywall = async (paywall) => {
  // Capture the target state first: setPaywallProp mutates the reactive
  // paywall.enabled, so reading it after the dispatch reverses the message.
  const willEnable = !paywall.enabled
  try {
    await PaywallService.toggleStreamPaywall(paywall.id)
    store.dispatch('Paywalls/setPaywallProp', { id: paywall.id, prop: 'enabled', value: willEnable })
    notify({ group: 'success', text: willEnable ? 'Paywall enabled' : 'Paywall disabled' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't update paywall" })
  }
}
const requestDeletePaywall = (paywall) => {
  deleteTargetId.value = paywall.id
  deleteTargetPaywall.value = paywall
  showModal('delete-paywall')
}

const onSourceStreamSelected = async (stream) => {
  const rand = Math.random().toString(36).slice(-7)
  clearTimeout(sourceSavedTimeout)
  sourceJustSaved.value = false
  sourceSaving.value = true
  try {
    const base = process.env.VUE_APP_PLAYER_APP_BASE_URL
    let url
    if (stream.streams) {
      // playlist
      url = `${base}/playlist/${stream._id}?paywall=1&rand=${rand}`
    } else if (stream.fileName) {
      // single VOD file: prefer the file's embedUrl, else fall back to /vod/<videoId>
      let vid = stream.id || stream._id || ''
      if (typeof vid === 'string' && vid.endsWith('.mp4')) vid = vid.replace('.mp4', '')
      url = stream.embedUrl
        ? `${stream.embedUrl}${stream.embedUrl.includes('?') ? '&' : '?'}paywall=1&rand=${rand}`
        : `${base}/vod/${vid}?paywall=1&rand=${rand}`
    } else if (stream.type === 'vod' && stream.subfolder?._id) {
      // sub-folder inside a VOD folder: the player scopes the playlist to it
      url = `${base}/vod/playlist/${stream._id}?paywall=1&subfolder=${stream.subfolder._id}&rand=${rand}`
    } else if (stream.type === 'vod') {
      // whole VOD folder
      url = `${base}/vod/playlist/${stream._id}?paywall=1&rand=${rand}`
    } else {
      // livestream
      url = `${base}/${stream.key}?paywall=1&rand=${rand}`
    }
    const payload = { url }

    await PaywallService.updateStreamPaywallSource(
      currentPaywall.value.id,
      stream._id || stream.id,
      payload
    );

    store.dispatch('Paywalls/setStreamSource', { id: currentPaywall.value.id, stream })

    sourceJustSaved.value = true
    sourceSavedTimeout = setTimeout(() => { sourceJustSaved.value = false }, 3000)

    let sourceType = stream.type
    if (stream.streams) sourceType = 'playlist'
    else if (stream.fileName) sourceType = 'vod'
    else if (!sourceType) sourceType = 'live'
    track(EVENTS.PAYWALL_SOURCE_ASSIGNED, {
      paywallId: currentPaywall.value.id,
      sourceType,
    })

    notify({
      group: "success",
      text: "Paywall source updated",
    });
  } catch (err) {
    console.error('Paywall source update failed', { id: currentPaywall.value?.id, sourceId: stream?._id, stream, err })
    notify({
      group: "error",
      text: err.message || "Couldn't update source",
    });
  } finally {
    sourceSaving.value = false
  }
}

const tempLinksFiltered = () => {
  const now = new Date().getTime();
  const filtered = _.filter(tempLinks.value, (item) => {
    return new Date(item.expireAt).getTime() > now;
  });

  return _.sortBy(filtered, function (d) {
    return new Date(d.expireAt);
  });
}
const generateTempLink = async () => {
  tempLinkProcessing.value = true;
  try {
    const expireAt = new Date(tempLinkDateTime.value).getTime();
    const expireMin = Math.round((expireAt - new Date().getTime()) / 60000);

    const resp = await PaywallService.createTemporaryLink(
      currentPaywall.value.source._id,
      expireAt,
      expireMin
    );
    tempLinks.value.push({
      url: resp.link,
      expireAt,
    });

    showTempLinkDatePicker.value = null;
    tempLinkDateTime.value = null;
    notify({
      group: "success",
      text: "Temporary link generated",
    });
  } catch (err) {
    console.error("err", err);
    notify({
      group: "error",
      text: err.message || "Couldn't generate link",
    });
  }
  tempLinkProcessing.value = false;
}

const setRangesTempLinkDateTime = () => {
  tempLinkMaxDateTime.value = new Date(
    Date.now() + 1 * 1000 * 60 * 60 * 24 * 30
  ).toISOString();
  tempLinkMinDateTime.value = new Date(
    Date.now() + 180 * 1000
  ).toISOString();
}

const humanDate = (date) => {
  return moment(new Date(date)).format("MMM DD, YYYY, [at] hh:mm A");
}

const paywallCreated = (paywall) => {
  notify({
    group: "success",
    text: "Paywall created",
  });

  setTimeout(() => {
    router.push({ path: '/paywall/v2/' + paywall.id }).catch(() => { });

    setTimeout(() => {
      window.location.reload()
    }, 200);
  }, 100)
}

const selectPaywall = async (paywallId) => {
  currentPaywallIndex.value = paywallId
  // Back-nav to the grid clears :paywallId, so the route-derived currentPaywall
  // getter resolves to undefined. Bail before dereferencing it.
  if (!paywallId || !currentPaywall.value) return
  displayPaywallReport()
  paywallName.value = currentPaywall.value.internalName || currentPaywall.value.title
  displayTitle.value = currentPaywall.value.title || ''
  paywallPrice.value = currentPaywall.value.price
  syncEditPaymentType()
  syncEditAccessLength()
  companyUrl.value = currentPaywall.value.company_url
  logoSrc.value = currentPaywall.value.company_logo
  companyName.value = currentPaywall.value.company_name
  companyEmail.value = currentPaywall.value.company_email
  popupAuth.value = currentPaywall.value.popupAuth
  const sourceId = currentPaywall.value?.source?._id
  if (!sourceId) {
    tempLinks.value = []
    return
  }
  // Temporary links are not needed to paint the shell.
  PaywallService.getTemporaryLinks(sourceId).then((resp) => {
    if (currentPaywall.value?.source?._id !== sourceId) return
    tempLinks.value = resp?.links || []
  }).catch(() => {})
}

const changeRouteQuery = (item) => {
  if (route.query.paywallTab != item) {
    router.replace({ query: { ...route.query, paywallTab: item } })
  } else return
}

const onPriceEdit = () => {
  showEditPriceInput.value = true
}
const blockNegativeKeys = (e) => {
  if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault()
}
// Internal (admin) name shown at the top / in the dashboard. Falls back to the
// display title for paywalls created before the name/title split.
const currentInternalName = () => currentPaywall.value?.internalName || currentPaywall.value?.title || ''
const onNameEdit = async () => {
  paywallName.value = currentInternalName()
  showEditNameInput.value = true
  await nextTick()
  paywallNameInput.value?.focus()
}
const cancelNameEdit = () => {
  paywallName.value = currentInternalName()
  showEditNameInput.value = false
}
const onNameUpdate = async () => {
  if (!showEditNameInput.value || savingName.value) return
  const value = (paywallName.value || '').trim()
  if (!value || value === currentInternalName()) {
    cancelNameEdit()
    return
  }
  savingName.value = true
  try {
    await PaywallService.updatePaywallInternalName(currentPaywall.value.id, value)
    store.dispatch('Paywalls/setPaywallProp', { id: currentPaywall.value.id, prop: 'internalName', value })
    showEditNameInput.value = false
    notify({ group: 'success', text: 'Paywall name updated' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't update name" })
  } finally {
    savingName.value = false
  }
}

// Viewer-facing display title (the headline on the paywall) = stream title.
const displayTitle = ref('')
const savingDisplayTitle = ref(false)
const syncDisplayTitle = () => { displayTitle.value = currentPaywall.value?.title || '' }
const onDisplayTitleUpdate = async () => {
  if (savingDisplayTitle.value) return
  // Blank display title falls back to the paywall (internal) name, so viewers
  // always see a heading. Reflect the resolved value back into the field.
  const value = (displayTitle.value || '').trim() || currentInternalName()
  if (value === currentPaywall.value?.title) {
    displayTitle.value = value
    return
  }
  savingDisplayTitle.value = true
  try {
    await PaywallService.updateStreamPaywallById(currentPaywall.value.id, { title: value })
    store.dispatch('Paywalls/setPaywallProp', { id: currentPaywall.value.id, prop: 'title', value })
    displayTitle.value = value
    notify({ group: 'success', text: 'Display title updated' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't update display title" })
  } finally {
    savingDisplayTitle.value = false
  }
}
// Only reacts to user-initiated select changes (FormSelect emits native change).
const onEditPaymentTypeChange = () => {
  if (editPaymentType.value === 'free') {
    paywallPrice.value = 0
    onPriceUpdate()
  }
}
const onAccessLengthSelectChange = () => {
  // Switching to lifetime drops the event-anchored start (it only applies to a window).
  if (editAccessLength.value === 'lifetime') {
    editAccessStartAt.value = null
    onAccessLengthUpdate()
  }
}
// Lower bound for the "Access starts on" picker: now, as a UTC ISO string to
// match the picker's `utc` mode (the temp-link picker uses the same shape).
// Refreshed when the field is focused so it never lags behind real time.
const accessStartMinDate = ref(new Date().toISOString())
const refreshAccessStartMinDate = () => { accessStartMinDate.value = new Date().toISOString() }
// A newly-picked start must not be in the past. Compare epoch millis (both the
// utc picker's ISO and Date.now() are UTC instants, so this is timezone-safe)
// with a 1-minute grace so selecting the current minute isn't wrongly rejected.
const ACCESS_START_PAST_GRACE_MS = 60 * 1000
const isPastDateTime = (v) => {
  if (!v) return false
  const t = new Date(v).getTime()
  return Number.isFinite(t) && t < Date.now() - ACCESS_START_PAST_GRACE_MS
}
// VueDatePicker in `utc` mode emits a UTC ISO string (or null); the input itself
// is shown in the creator's local timezone. Persist the (optional) event-anchored
// start; null = starts at purchase time. Reject a freshly-picked past value; an
// existing (already-saved) past start is left untouched so editing doesn't break.
const onAccessStartChange = (val) => {
  if (val && isPastDateTime(val)) {
    notify({ group: 'error', text: "Access start date can't be in the past" })
    editAccessStartAt.value = currentPaywall.value?.access_start_at || null
    return
  }
  editAccessStartAt.value = val || null
  if (editAccessLength.value === 'limited') onAccessLengthUpdate()
}
// Normalize the model value to a UTC ISO string for the payload (the utc picker
// already emits one; this also guards against a Date/invalid value). null = no start.
const toUtcIso = (v) => {
  if (!v) return null
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}
const onAccessLengthUpdate = async () => {
  if (savingAccess.value) return
  savingAccess.value = true
  const value = editAccessSeconds.value
  // Only send a start date for a limited window; otherwise clear it.
  const startAt = editAccessLength.value === 'limited' ? toUtcIso(editAccessStartAt.value) : null
  try {
    await PaywallService.updateStreamPaywallById(currentPaywall.value.id, {
      access_seconds: value,
      access_start_at: startAt,
    })
    store.dispatch('Paywalls/setPaywallProp', { id: currentPaywall.value.id, prop: 'access_seconds', value })
    store.dispatch('Paywalls/setPaywallProp', { id: currentPaywall.value.id, prop: 'access_start_at', value: startAt })
    notify({ group: 'success', text: 'Access settings updated' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't update access settings" })
  } finally {
    savingAccess.value = false
  }
}
const onPriceUpdate = async () => {
  // Backend price column is Decimal(8,2) (and Stripe caps here), so 999,999.99 is the max.
  let price = Number(paywallPrice.value)
  if (isNaN(price) || price < 0) price = 0
  if (price > MAX_PRICE) {
    price = MAX_PRICE
    notify({ group: 'error', text: `Maximum amount is ${formatMoney(MAX_PRICE, currentPaywall.value?.currency)}` })
  }
  paywallPrice.value = price
  try {
    await PaywallService.updateStreamPaywallById(currentPaywall.value.id, { price: Number(paywallPrice.value) })
    store.dispatch('Paywalls/setPaywallProp', { id: currentPaywall.value.id, prop: 'price', value: Number(paywallPrice.value) })
    syncEditPaymentType()
    showEditPriceInput.value = false

    track(EVENTS.PAYWALL_PRICE_UPDATED, {
      paywallId: currentPaywall.value.id,
      price: Number(paywallPrice.value),
      currency: currentPaywall.value?.currency,
    })

    notify({
      group: "success",
      text: "Price updated",
    });
  } catch (err) {
    notify({
      group: 'error',
      text: err.message || "Couldn't update price",
    })
  }
}

const clipboardCopy = async (text) => {
  try {
    if (text instanceof Function) text = text();

    await navigator.clipboard.writeText(text);
    notify({ group: 'info', text: 'Copied to clipboard' });
  } catch (e) { }
}

const trackPaywallEmbedCopied = () => {
  track(EVENTS.EMBED_CODE_COPIED, { contentType: 'paywall', copyType: 'embed' })
}
const trackPaywallUrlCopied = () => {
  track(EVENTS.CONTENT_URL_COPIED, { contentType: 'paywall' })
}

const handleDragging = (e) => {
  e.preventDefault();
  const position = e.pageX
  const wrapper = document.getElementById('resizible-wrapper')
  const windowWidth = window.innerWidth
  const percentage = ((position - 260) / wrapper.offsetWidth) * 100
  if (percentage >= 20 && percentage <= 30) {
    dividerPosition.value = percentage.toFixed(2)
  }
  if (position - 260 <= 0) {
    document.removeEventListener('mousemove', handleDragging.value)
  }
}
const startDragging = () => {
  document.addEventListener('mousemove', handleDragging.value)
}

const endDragging = () => {
  document.removeEventListener('mousemove', handleDragging.value)
}

const isTabActive = (tabName, isActive = false) => {
  if (route.query.paywallTab) return route.query?.paywallTab === tabName
  return isActive
}

const paywallTabs = [
  { id: 'setup', label: 'Setup', isDefault: true },
  { id: 'branding', label: 'Branding' },
  { id: 'access', label: 'Access' },
  { id: 'promo', label: 'Promo' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'customers', label: 'Customers' },
]


</script>

<template>
  <div id="resizible-wrapper" class="container detail-shell px-4" @mouseup="endDragging()">
    <PageHeader
      v-if="showPaywallChromeHeader"
      title="Paywall"
      subtitle="Monetize your content with pay-per-view and subscriptions."
    />
    <Spinner v-if="loadingStatus || !userloaded" text="Loading your paywalls…" classes="text-dark-8 mh-60"
      spinner-color="var(--c-dark-8)" spinner-size="15px" />
    <main v-else-if="!canAccessPaywall">
      <NoAccessState :owner-email="ownerEmail" />
    </main>
    <main v-else-if="paywalls?.length > 0 && !isStripePending">
      <!-- List: card-grid dashboard -->
      <PaywallDashboardGrid v-if="showGrid && !showSettings" :paywalls="paywalls" :reports="paywallReports || []"
        :users="paywallUsers" :stripe-id="userPaywall?.externalMasterId" :commission="paywallStripeCommission"
        :max-paywalls="maxPaywalls"
        @open="openPaywall"
        @toggle="togglePaywall" @delete="requestDeletePaywall" @create="showModal('add-paywall-modal')"
        @unlink-stripe="requestUnlinkStripe" @switch-stripe="requestSwitchStripe"
        @open-settings="openSettings" />

      <!-- Global settings (inline, same route) -->
      <PaywallGlobalSettings v-else-if="showSettings" @close="closeSettings" />

      <!-- Detail: existing master-detail view -->
      <template v-else>
        <div class="pb-10">
          <!-- Header -->
          <header class="flex flex-wrap items-start gap-3 mt-4 mb-5">
            <!-- Keep back + title on one row; long subtitles must truncate inside this flex-1 group. -->
            <div class="flex min-w-0 flex-1 items-start gap-3">
              <BackButton title="Back to paywalls" class="mt-2 shrink-0" @click="goToGrid" />
              <div class="min-w-0 flex-1 flex items-start gap-2.5">
              <span class="mt-2 size-2 shrink-0 rounded-full"
                :class="currentPaywall?.enabled ? 'bg-success' : 'bg-surface-5'"></span>
              <div class="min-w-0 flex-1">
                <div v-if="!showEditNameInput" class="flex items-center gap-1.5 min-w-0">
                  <h1 class="text-lg font-semibold text-white mb-0 truncate">{{ currentPaywall?.internalName || currentPaywall?.title }}</h1>
                  <FormButton type="link" isIcon size="sm" title="Rename paywall"
                    class="shrink-0 text-surface-7 hover:!text-white" @click="onNameEdit">
                    <icon-base fill="none">
                      <icon-edit />
                    </icon-base>
                  </FormButton>
                </div>
                <FormInput v-else ref="paywallNameInput" v-model="paywallName" class="max-w-xs" :maxlength="100"
                  @keyup.enter="onNameUpdate" @keyup.esc="cancelNameEdit" @blur="onNameUpdate" />
                <p class="text-tiny text-surface-7 mb-0 truncate">{{ paywallSubtitle }}</p>
              </div>
              </div>
            </div>
            <div class="flex basis-full flex-wrap items-center justify-end gap-2 sm:basis-auto sm:shrink-0 sm:pl-0">
              <Tooltip v-if="!currentPaywall?.source" content="No source set">
                <icon-base class="text-gold">
                  <icon-info-small />
                </icon-base>
              </Tooltip>
              <FormButton type="secondary" size="sm" isIcon title="Open protected page"
                :href="currentPaywall?._streamUrl?.replace('_disabled', '')" target="_blank">
                <icon-base fill="none">
                  <icon-link-external />
                </icon-base>
              </FormButton>
              <ButtonCopyToClipboard :text="currentPaywall?._streamUrl?.replace('_disabled', '')" @click="trackPaywallUrlCopied" />
              <FormButton type="secondary" size="sm" @click="togglePaywall(currentPaywall)">
                {{ currentPaywall?.enabled ? 'Disable' : 'Enable' }}
              </FormButton>
              <FormButton type="secondary" size="sm" isIcon title="Delete" class="hover:!text-danger"
                @click="requestDeletePaywall(currentPaywall)">
                <icon-base fill="none">
                  <icon-bin />
                </icon-base>
              </FormButton>
            </div>
          </header>

          <!-- Stat cards -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <!-- Net earnings (primary) -->
            <div class="bg-surface-2 rounded-xl ring-1 ring-white/[0.05] p-4" data-testid="paywall-stat-net-earnings">
              <div class="flex items-center justify-between gap-2 mb-1">
                <span class="text-[10px] font-semibold uppercase tracking-wider text-surface-6">Net earnings</span>
                <Badge v-if="paywallStripeCommission" size="xs">{{ paywallStripeCommission }}% commission</Badge>
              </div>
              <p class="text-xl font-semibold text-white mb-0 truncate">{{ formatMoney(totalEarnings, currentPaywall?.currency) }}</p>
              <p class="text-tiny text-surface-6 mb-0 mt-1">After refunds</p>
            </div>
            <!-- Revenue (gross) -->
            <div class="bg-surface-2 rounded-xl ring-1 ring-white/[0.05] p-4" data-testid="paywall-stat-revenue">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-surface-6 mb-1">Revenue</p>
              <p class="text-xl font-semibold text-white mb-0 truncate">{{ formatMoney(totalPayments, currentPaywall?.currency) }}</p>
              <p class="text-tiny text-surface-6 mb-0 mt-1">{{ paymentsCount }} {{ paymentsCount === 1 ? 'payment' : 'payments' }}</p>
            </div>
            <!-- Refunds -->
            <div class="bg-surface-2 rounded-xl ring-1 ring-white/[0.05] p-4" data-testid="paywall-stat-refunds">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-surface-6 mb-1">Refunds</p>
              <p class="text-xl font-semibold text-white mb-0 truncate">{{ formatMoney(totalRefunds, currentPaywall?.currency) }}</p>
              <p class="text-tiny text-surface-6 mb-0 mt-1">{{ refundsCount }} {{ refundsCount === 1 ? 'refund' : 'refunds' }}</p>
            </div>
            <!-- Customers -->
            <div class="bg-surface-2 rounded-xl ring-1 ring-white/[0.05] p-4" data-testid="paywall-stat-customers">
              <p class="text-[10px] font-semibold uppercase tracking-wider text-surface-6 mb-1">Customers</p>
              <p class="text-xl font-semibold text-white mb-0">{{ customersCount }}</p>
              <p class="text-tiny text-surface-6 mb-0 mt-1">Paying viewers</p>
            </div>
          </div>

          <!-- Tabs + content card -->
          <div class="bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-4 md:p-5">
          <!-- Tabs -->
          <TabGroup type="segmented" custom-classes="overflow-x-auto no-scrollbar">
            <TabItem v-for="tab in paywallTabs" :key="tab.id"
              :is-active="isTabActive(tab.id, tab.isDefault)"
              @click="changeRouteQuery(tab.id)">
              {{ tab.label }}
            </TabItem>
          </TabGroup>

          <div class="pt-6" v-if="currentPaywall">
            <!-- SETUP -->
            <div v-if="isTabActive('setup', true)" class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <div class="flex flex-col gap-4 min-w-0">
              <!-- Source -->
              <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
                <div class="flex items-start gap-2 mb-3">
                  <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-tiny font-semibold"
                    :class="currentPaywall?.source ? 'bg-success-transparent text-success' : 'bg-surface-3 text-surface-7'">1</span>
                  <div class="min-w-0">
                    <h3 class="text-sm font-semibold text-white mb-0.5">Content</h3>
                    <p class="text-tiny text-surface-7 mb-0">Set the viewer-facing title and choose the stream or playlist this paywall protects.</p>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="block text-tiny font-medium text-surface-8 mb-1">Display title</label>
                  <FormInput v-model="displayTitle" @change="onDisplayTitleUpdate" size="md"
                    placeholder="Uses the paywall name" :maxlength="120" />
                  <p class="text-tiny text-surface-6 mb-0 mt-1">The headline viewers see on the paywall. Leave blank to use the paywall name.</p>
                </div>
                <label class="block text-tiny font-medium text-surface-8 mb-1">Content source</label>
                <stream-source-dropdown @on-show="onSourceDropdownShow" @on-hide="onSourceDropdownHide"
                  @on-source-selected="onSourceStreamSelected" :dropRight="false" :key="currentPaywall?.id"
                  :selectedStream="currentPaywall?.source" :showPlaylist="true" source="paywall" />
                <!-- Inline save feedback so users see the source update without relying on the toast -->
                <div class="mt-2 flex items-center gap-1.5 text-tiny min-h-[1.125rem] min-w-0">
                  <template v-if="sourceSaving">
                    <Spinner spinner-size="12px" spinner-color="var(--c-dark-8)" classes="!justify-start" />
                    <span class="text-surface-7">Updating source…</span>
                  </template>
                  <template v-else-if="sourceJustSaved">
                    <icon-base width="14" height="14" class="text-success shrink-0">
                      <icon-checkmark />
                    </icon-base>
                    <span class="text-success">Source updated</span>
                  </template>
                  <template v-else-if="currentPaywall?.source">
                    <icon-base width="14" height="14" class="text-success shrink-0">
                      <icon-checkmark />
                    </icon-base>
                    <span class="text-surface-7 shrink-0">Protecting</span>
                    <span
                      class="text-surface-text-1 font-medium min-w-0 truncate"
                      :title="sourceDisplayName(currentPaywall.source)"
                    >{{ sourceDisplayName(currentPaywall.source) }}</span>
                  </template>
                  <span v-else class="text-gold">No source selected yet</span>
                </div>
              </section>

              <!-- Pricing -->
              <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
                <div class="flex items-start gap-2 mb-3">
                  <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-tiny font-semibold"
                    :class="currentPaywall ? 'bg-success-transparent text-success' : 'bg-surface-3 text-surface-7'">2</span>
                  <div class="min-w-0">
                    <h3 class="text-sm font-semibold text-white mb-0.5">Pricing</h3>
                    <p class="text-tiny text-surface-7 mb-0">Set how much viewers pay for one-time access.</p>
                  </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-tiny font-medium text-surface-8 mb-1">Payment type</label>
                    <FormSelect v-model="editPaymentType" :options="paymentTypeOptions" @change="onEditPaymentTypeChange" />
                  </div>
                  <div>
                    <label class="block text-tiny font-medium text-surface-8 mb-1">Amount</label>
                    <!-- Currency is fixed at creation, so show it as a prefix instead of a fake-editable field. -->
                    <div class="relative">
                      <span class="absolute inset-y-0 left-0 z-10 flex items-center pl-3 text-tiny font-medium text-surface-9 pointer-events-none">
                        {{ currencyUpper }}
                      </span>
                      <FormInput type="number" v-model="paywallPrice" @change="onPriceUpdate"
                        @keydown="blockNegativeKeys" :disabled="editPaymentType === 'free'"
                        customClasses="!pl-12 paywall-amount-input" />
                    </div>
                  </div>
                </div>
                <p class="text-tiny text-surface-6 mb-0 mt-2">
                  {{ editPaymentType === 'free' ? 'This paywall is free — viewers get access with no payment.' : `Charged in ${currencyUpper}, set at creation.` }}
                </p>

                <!-- Access length (paid only) — same field style as above to blend in -->
                <div v-if="editPaymentType !== 'free'" class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label class="block text-tiny font-medium text-surface-8 mb-1">Access length</label>
                    <FormSelect v-model="editAccessLength" :options="accessLengthOptions"
                      @change="onAccessLengthSelectChange" />
                  </div>
                  <div v-if="editAccessLength === 'limited'">
                    <label class="block text-tiny font-medium text-surface-8 mb-1">Duration</label>
                    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <FormInput type="number" min="1" v-model="editAccessValue" @change="onAccessLengthUpdate"
                        @keydown="blockNegativeKeys" customClasses="paywall-amount-input" />
                      <FormSelect v-model="editAccessUnit" :options="accessUnitOptions" @change="onAccessLengthUpdate" />
                    </div>
                  </div>
                  <!-- Optional event-anchored start: access runs for the duration above,
                       counted from this date (early buyers don't expire before it). -->
                  <div v-if="editAccessLength === 'limited'">
                    <label class="flex items-center gap-1 text-tiny font-medium text-surface-8 mb-1">
                      <span>Access starts on <span class="text-surface-6 font-normal">(optional)</span></span>
                      <Tooltip
                        content="Set this to your event date so access covers the event even for early buyers — e.g. a Saturday game with 2-day access stays available through Saturday + 2 days. Leave empty to start access at purchase.">
                        <icon-base class="text-surface-6 hover:text-surface-8 transition-colors" width="14" height="14">
                          <icon-info-small />
                        </icon-base>
                      </Tooltip>
                    </label>
                    <div class="flex items-center relative">
                      <IconBase class="absolute z-10 !left-2 text-surface-8">
                        <icon-calendar />
                      </IconBase>
                      <IconBase class="absolute z-10 !left-7 text-surface-8">
                        <icon-clock />
                      </IconBase>
                      <VueDatePicker placeholder="Select start date (optional)" :model-value="editAccessStartAt" utc
                        :clearable="true" :min-date="accessStartMinDate" @focus="refreshAccessStartMinDate"
                        @update:model-value="onAccessStartChange"
                        inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14 h-8 text-tiny font-inter" />
                    </div>
                    <p class="text-tiny text-surface-7 mb-0 mt-1">
                      Times in your timezone — {{ localTimezoneLabel }}.
                    </p>
                  </div>
                  <p class="text-tiny text-surface-6 mb-0 sm:col-span-2">
                    {{ editAccessLength === 'lifetime'
                      ? 'Viewers keep access for up to a year after paying.'
                      : (editAccessStartAt
                        ? `Access runs for ${editAccessSummary} starting on the date above; buyers before then keep access until it expires. Applies to new purchases only.`
                        : `Viewers get access for ${editAccessSummary}, then it expires. Applies to new purchases only.`) }}
                  </p>
                </div>
              </section>

              <!-- Share -->
              <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
                <div class="flex items-start gap-2 mb-3">
                  <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-tiny font-semibold"
                    :class="currentPaywall ? 'bg-success-transparent text-success' : 'bg-surface-3 text-surface-7'">3</span>
                  <div class="min-w-0">
                    <h3 class="text-sm font-semibold text-white mb-0.5">Share your paywall</h3>
                    <p class="text-tiny text-surface-7 mb-0">Embed the paywall on your own site, or share a direct link.</p>
                  </div>
                </div>

                <div class="flex flex-col gap-4">
                  <!-- Embed code (inline paywall on any site) -->
                  <div v-if="paywallEmbedCode" class="rounded-lg bg-surface-2 p-4">
                    <h4 class="text-sm font-medium text-white mb-1">Embed on your website</h4>
                    <p class="text-tiny text-surface-7 mb-2">Drop this snippet into your site — viewers register, pay, and watch inline.</p>
                    <div class="relative">
                      <pre class="overflow-x-auto rounded-lg bg-surface-1 p-3 pr-10 text-[11px] leading-relaxed text-surface-8 no-scrollbar whitespace-pre-wrap break-all mb-0"><code>{{ paywallEmbedCode }}</code></pre>
                      <ButtonCopyToClipboard class="absolute right-2 top-2 z-10" :text="paywallEmbedCode" @click="trackPaywallEmbedCopied" />
                    </div>
                  </div>

                  <!-- Direct URL -->
                  <div class="rounded-lg bg-surface-2 p-4">
                    <h4 class="text-sm font-medium text-white mb-1">Direct URL</h4>
                    <p class="text-tiny text-surface-7 mb-2">Share this link directly — viewers register, pay, and watch on your hosted paywall page.</p>
                    <div class="relative">
                      <FormInput customClasses="!bg-surface-1 !text-surface-8 !pr-16 truncate"
                        :modelValue="currentPaywall?._streamUrl?.replace('_disabled', '')"
                        @click="clipboardCopy(currentPaywall._streamUrl.replace('_disabled', '')); trackPaywallUrlCopied()" readonly />
                      <ButtonCopyToClipboard class="absolute right-8 top-0 z-10"
                        :text="currentPaywall?._streamUrl?.replace('_disabled', '')" @click="trackPaywallUrlCopied" />
                      <FormButton class="absolute z-10 right-2 top-0" size="sm" type="link" isIcon
                        :href="currentPaywall?._streamUrl?.replace('_disabled', '')" target="_blank">
                        <icon-base>
                          <icon-link-external />
                        </icon-base>
                      </FormButton>
                    </div>
                  </div>
                </div>

              </section>
              </div>

              <!-- Live preview -->
              <PaywallLivePreview :has-source="!!currentPaywall?.source" :protected-url="protectedUrl"
                :srcdoc="embedPreviewSrcdoc" :refresh-key="previewKey" />
            </div>

            <!-- BRANDING -->
            <div v-else-if="isTabActive('branding')" class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <div class="flex flex-col gap-4 min-w-0">
                <PaywallEventPage :current-paywall="currentPaywall" :key="currentPaywall.id" />
                <PaywallCallToAction :current-paywall="currentPaywall" />
                <PaywallInterface :current-paywall="currentPaywall" />
                <PaywallSettings :current-paywall="currentPaywall" />
              </div>
              <!-- Live preview -->
              <PaywallLivePreview :has-source="!!currentPaywall?.source" :protected-url="protectedUrl"
                :srcdoc="embedPreviewSrcdoc" :refresh-key="previewKey" />
            </div>

            <!-- ACCESS -->
            <div v-else-if="isTabActive('access')" class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <div class="flex flex-col gap-4 min-w-0">
              <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
                <h4 class="text-sm font-semibold text-white mb-0.5">Temporary access links</h4>
                <p class="text-tiny text-surface-7 mb-3">Temporary links let viewers watch without signing up or logging
                  in, and expire at the time you choose.</p>
                <template v-if="showTempLinkDatePicker">
                  <label class="block text-sm text-surface-8 mb-1">Expiration date</label>
                  <div class="flex items-center relative mb-2">
                    <IconBase class="absolute z-10 !left-2 text-surface-8">
                      <icon-calendar />
                    </IconBase>
                    <IconBase class="absolute z-10 !left-7 text-surface-8">
                      <icon-clock />
                    </IconBase>
                    <VueDatePicker placeholder="Select date time" v-model="tempLinkDateTime" utc
                      :min-date="tempLinkMinDateTime" :max-date="tempLinkMaxDateTime" @focus="setRangesTempLinkDateTime"
                      inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14 h-8 text-tiny font-inter" />
                  </div>
                </template>
                <div class="flex flex-col gap-2 sm:flex-row">
                  <Tooltip v-show="!showTempLinkDatePicker" :active="!currentPaywall?.source"
                    content="Please set a source to have this feature">
                    <span class="inline-flex">
                      <FormButton label="Generate" :disabled="!currentPaywall?.source"
                        @click="showTempLinkDatePicker = true" />
                    </span>
                  </Tooltip>
                  <FormButton v-show="showTempLinkDatePicker" :label="tempLinkProcessing ? 'Generating…' : 'Generate link'"
                    :disabled="!tempLinkDateTime || !currentPaywall?.source" @click="generateTempLink" />
                  <FormButton v-show="showTempLinkDatePicker" type="danger" label="Cancel"
                    @click="showTempLinkDatePicker = false; tempLinkDateTime = null;" />
                </div>
                <ul class="mt-4 flex flex-col gap-3">
                  <li v-for="(item, idx) in tempLinksFiltered()" :key="idx">
                    <h5 class="text-sm text-surface-8 !mb-1">Expires {{ humanDate(item.expireAt) }}</h5>
                    <div class="relative">
                      <FormInput customClasses="!pr-10 truncate" :modelValue="item.url" @click="clipboardCopy(item.url)"
                        readonly />
                      <ButtonCopyToClipboard class="absolute right-2 top-0 z-10" :text="item.url" />
                    </div>
                  </li>
                </ul>
              </section>
              <PaywallSessionLimit :current-paywall="currentPaywall" />
              <PaywallSharedAccess :current-paywall="currentPaywall" />
              <PaywallEmailAccess :current-paywall="currentPaywall" />
              </div>
              <!-- Live preview -->
              <PaywallLivePreview :has-source="!!currentPaywall?.source" :protected-url="protectedUrl"
                :srcdoc="embedPreviewSrcdoc" :refresh-key="previewKey" />
            </div>

            <!-- PROMO -->
            <div v-else-if="isTabActive('promo')" class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <div class="flex flex-col gap-4 min-w-0">
                <PaywallPromo :current-paywall="currentPaywall" :key="currentPaywall.id" />
              </div>
              <!-- Live preview -->
              <PaywallLivePreview :has-source="!!currentPaywall?.source" :protected-url="protectedUrl"
                :srcdoc="embedPreviewSrcdoc" :refresh-key="previewKey" />
            </div>

            <!-- TRANSACTIONS -->
            <PaywallTransactions v-else-if="isTabActive('transactions')" :paywallStreamId="currentPaywall.id"
              :key="currentPaywall.id" />

            <!-- CUSTOMERS -->
            <PaywallCustomers v-else-if="isTabActive('customers')" :paywallStreamId="currentPaywall.id"
              :key="currentPaywall.id" />
          </div>
          </div>
        </div>
      </template>
    </main>
    <PaywallPlaceholder v-else :onStripeConnectClick="onStripeConnectClick"
      :onStripeConnectCancelClick="onStripeConnectCancelClick" :stripeConnectProcessing="stripeConnectProcessing"
      :stripeOauthLink="stripeOauthLink" :stripeConnectLinkError="stripeConnectLinkError"
      :paywallActive="paywallActive" :isStripePending="isStripePending"
      :stripeStatus="paywallInfo?.stripe_status"
      @unlink-stripe="requestUnlinkStripe" @switch-stripe="requestSwitchStripe" />

    <add-paywall-modal @paywall-created="paywallCreated" />
    <demo-video-modal videoid="1" />
    <confirm-modal modalId="delete-paywall" message="Delete this paywall?"
      body="This permanently removes the paywall and its settings. This action can't be undone." modal-type="danger"
      okText="Delete" cancelText="Cancel" @modal-confirm="onPaywallRemove" />
    <confirm-modal modalId="paywall-has-payments" message="This paywall can't be deleted"
      body="Because it has associated payments, it's retained to preserve your transaction history. You can disable it instead to stop accepting new payments."
      okText="Disable paywall" cancelText="Keep it" @modal-confirm="disablePaywallAfterFailedDelete" />
    <confirm-modal modalId="unlink-stripe" message="Unlink Stripe account?"
      :body="unlinkStripeBody"
      okText="Unlink" cancelText="Cancel" modal-type="danger" @modal-confirm="onUnlinkStripe" />
    <confirm-modal modalId="switch-stripe" message="Use a different Stripe account?"
      body="This disconnects your current Stripe account so you can connect a new one. Your existing account and its transaction history stay in your Stripe dashboard."
      okText="Continue" cancelText="Cancel" @modal-confirm="onSwitchStripeAccount" />
    <alert-modal modalId="stripe-paywall-warning" :message="stripeWarningMessage"
      :body="stripeWarningBody" okText="Got it" />
    <confirm-modal modalId="toggle-popupauth" message="Source conflict"
      body="Enabling the paywall for this player will disable any other paywalls using the same stream source. Do you want to continue?"
      okText="Continue" cancelText="Cancel" @modal-confirm="togglePopupAuth" @modal-cancel="togglePopupAuthCancel" />
  </div>
</template>

<style>
.stream-source-menu {
  background: linear-gradient(0deg, #232D4D, #232D4D), linear-gradient(0deg, #343E61, #343E61);
}

/* Amount is entered manually — hide the number spinners. */
.paywall-amount-input::-webkit-outer-spin-button,
.paywall-amount-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.paywall-amount-input {
  -moz-appearance: textfield;
}
</style>
